"use client";

import type { Dispatch, SetStateAction } from "react";
import { UserRole } from "@prisma/client";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import {
  BsBuildings,
  BsBuildingsFill,
  BsMailbox,
  BsMailbox2Flag,
} from "react-icons/bs";
import { IoLayers, IoLayersOutline } from "react-icons/io5";
import { LuClub } from "react-icons/lu";
import {
  PiGear,
  PiGearFill,
  PiUsersThreeFill,
  PiUsersThreeLight,
} from "react-icons/pi";
import {
  RiFileList3Fill,
  RiFileList3Line,
  RiHome9Fill,
  RiHome9Line,
  RiInstanceFill,
  RiInstanceLine,
  RiMoneyDollarCircleFill,
  RiMoneyDollarCircleLine,
  RiRobot2Fill,
  RiRobot2Line,
} from "react-icons/ri";
import {
  TbLayoutDashboard,
  TbLayoutDashboardFilled,
  TbLocation,
  TbLocationFilled,
  TbSquareRounded,
  TbSquareRoundedFilled,
} from "react-icons/tb";
import { TfiLayoutListThumb, TfiLayoutListThumbAlt } from "react-icons/tfi";

import { WorkActions } from "@/components/dashboard/work-actions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useProjectCreatedRedirect,
  useProjectItems,
} from "@/hooks/use-work-items";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/types";

interface DashboardNavProps {
  items: NavItem[];
  setOpen?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  isAdmin?: boolean;
  isProjectNav?: boolean;
  collapsed?: boolean;
  onClick?: () => void;
}

const Icons = {
  dashboard: {
    Stale: TbLayoutDashboard,
    Selected: TbLayoutDashboardFilled,
  },
  projects: {
    Stale: RiFileList3Line,
    Selected: RiFileList3Fill,
  },
  rateCards: {
    Stale: RiMoneyDollarCircleLine,
    Selected: RiMoneyDollarCircleFill,
  },
  people: {
    Stale: PiUsersThreeLight,
    Selected: PiUsersThreeFill,
  },
  project: {
    Stale: TbSquareRounded,
    Selected: TbSquareRoundedFilled,
  },
  tasks: {
    Stale: TfiLayoutListThumb,
    Selected: TfiLayoutListThumbAlt,
  },
  services: {
    Stale: RiInstanceLine,
    Selected: RiInstanceFill,
  },
  settings: {
    Stale: PiGear,
    Selected: PiGearFill,
  },
  office: {
    Stale: TbLocation,
    Selected: TbLocationFilled,
  },
  ai: {
    Stale: RiRobot2Line,
    Selected: RiRobot2Fill,
  },
  organization: {
    Stale: BsBuildings,
    Selected: BsBuildingsFill,
  },
  invites: {
    Stale: BsMailbox,
    Selected: BsMailbox2Flag,
  },
  data: {
    Stale: IoLayersOutline,
    Selected: IoLayers,
  },
  home: {
    Stale: RiHome9Line,
    Selected: RiHome9Fill,
  },
};

const WorkActionsInternal = ({ navItem }: { navItem: NavItem }) => {
  const { projectItems } = useProjectItems();

  const projectId = navItem.href.split("/")[2];
  const projectItem = projectItems?.find((item) => item.id === projectId);
  if (!projectItem) {
    return null;
  }

  return <WorkActions workItem={projectItem} compact verticalEllipsis />;
};

export function DashboardNav({
  items,
  setOpen,
  className,
  isProjectNav = false,
  collapsed = false,
  onClick,
}: Readonly<DashboardNavProps>) {
  useProjectCreatedRedirect();

  const path = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const session = useSession();

  const isSuperAdmin = session.data?.user?.role === UserRole.SUPER_ADMIN;
  const orgIdParam =
    typeof params.orgId === "string"
      ? params.orgId
      : Array.isArray(params.orgId)
        ? params.orgId[0]
        : undefined;

  //gets partial URL until OrgId
  //  const dashboardPath = path.split("/").slice(0, 3).join("/");

  if (!items?.length) {
    return null;
  }

  return (
    <nav
      className={cn(
        "grid items-start gap-1",
        className,
        !collapsed && !isProjectNav && "px-0",
        isProjectNav && "pr-0",
      )}
    >
      {items.map((item) => {
        const Icon = Icons[item.icon as keyof typeof Icons] || LuClub;

        // SA nav items are absolute (`/sa/super/...`). Do not wait on session —
        // otherwise a loading session prefixes `undefined` and RSC prefetch 404s.
        const href =
          item.href.startsWith("/sa/") ||
          path.includes("/sa/super") ||
          (isSuperAdmin && !orgIdParam)
            ? item.href
            : orgIdParam
              ? `/${orgIdParam}${item.href}`
              : item.href;

        // Preserve existing view unless the user is currently in full-screen chat,
        // in which case switch to split-view on navigation.
        const currentView = searchParams.get("view");
        const nextView =
          currentView === "chat" ? "split-view" : currentView || undefined;

        const baseEntries = Array.from(searchParams.entries()).filter(
          ([key]) => key !== "view",
        );
        const queryObj: Record<string, string> = Object.fromEntries(baseEntries);
        queryObj.status = "incomplete";
        if (nextView) {
          queryObj.view = nextView;
        }

        return (
          item.href && (
            <div
              key={item.href}
              className="group relative flex w-full min-w-0 items-center"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href={{
                      pathname: item.disabled ? "#" : href,
                      query: queryObj,
                    }}
                    onClick={() => {
                      setOpen?.(false);
                      onClick?.();
                    }}
                    className={cn(
                      collapsed
                        ? "flex size-10 items-center justify-center rounded-md text-foreground hover:bg-background/90"
                        : cn(
                            "min-w-0 flex-1 px-0",
                            isProjectNav &&
                              "flex w-full min-w-0 overflow-hidden",
                          ),
                    )}
                  >
                    <span
                      className={cn(
                        "group flex items-center rounded-md py-1 text-sm font-medium hover:bg-background/90 hover:text-accent-foreground",
                        collapsed
                          ? "justify-center px-0"
                          : !isProjectNav
                            ? "p-2 hover:bg-background/90"
                            : "w-full min-w-0 overflow-hidden pl-2 pr-0",
                        path === href ? "bg-background/90 font-bold" : "",
                        item.disabled && "cursor-not-allowed opacity-80",
                      )}
                    >
                      {path === href && !isProjectNav ? (
                        <Icon.Selected
                          className={cn(
                            "size-4 shrink-0",
                            collapsed ? "mx-0" : "mr-2",
                          )}
                        />
                      ) : !isProjectNav ? (
                        <Icon.Stale
                          className={cn(
                            "size-4 shrink-0",
                            collapsed ? "mx-0" : "mr-2",
                          )}
                        />
                      ) : (
                        ""
                      )}
                      {/* {isProjectNav && (
                        <>
                          {path === href ? (
                            <TbSquareRoundedFilled
                              className={cn(
                                "size-4 shrink-0",
                                collapsed ? "mx-0" : "mr-2",
                              )}
                            />
                          ) : (
                            <TbSquareRounded
                              className={cn(
                                "size-4 shrink-0",
                                collapsed ? "mx-0" : "mr-2",
                              )}
                            />
                          )}
                        </>
                      )} */}
                      {!collapsed && isProjectNav ? (
                        <span className="min-w-0 flex-1 overflow-hidden text-clip whitespace-nowrap pr-0 text-sm transition-[padding] duration-150 group-hover:pr-8 group-hover:text-ellipsis">
                          {item.title}
                        </span>
                      ) : !collapsed ? (
                        <span className="max-w-[170px] truncate text-sm">
                          {item.title}
                        </span>
                      ) : (
                        ""
                      )}
                    </span>
                  </Link>
                </TooltipTrigger>
                {/* commenting since we are seeing two tooltips */}
                <TooltipContent side="right">
                  <p>{item.title}</p>
                </TooltipContent>
              </Tooltip>
              {isProjectNav && (
                <div className="pointer-events-none absolute right-0 top-1/2 z-10 -translate-y-1/2 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
                  <WorkActionsInternal navItem={item} />
                </div>
              )}
            </div>
          )
        );
      })}
    </nav>
  );
}
